/**
 * Multiplex client for multi-feed channel subscriptions
 * Handles multi-feed subscriptions that stream updates from multiple sub-channels
 * Shares the same WebSocketClient instance with SubscriptionClient
 */

import { WebSocketClient } from "../../transport/websocket/WebSocketClient";
import {
  SubscriptionCallback,
  MultiFeedMessage,
  MultiFeedRaweEnvelope,
} from "../../types";

export class MultiplexClient {
  private wsClient: WebSocketClient;
  // Multi-feed subscriptions: map of sub_channel -> callbacks
  private multiFeedSubscriptions: Map<string, SubscriptionCallback[]> =
    new Map();
  // General multi-feed callbacks (for subscribeMultiFeed)
  private multiFeedCallbacks: SubscriptionCallback[] = [];
  private multiFeedHandlerRegistered: boolean = false;
  // Track multi-feed subscription state per marketId to avoid duplicate subscriptions
  // Use special key "auth-only" for subscriptions that don't require marketId (e.g., balances, orders, positions, buckets)
  private multiFeedSubscribedMarkets: Set<string> = new Set();

  constructor(wsClient: WebSocketClient) {
    this.wsClient = wsClient;
  }

  /**
   * Check if WebSocket is connected
   */
  private isConnected(): boolean {
    return this.wsClient.isConnected();
  }

  /**
   * Parameter validation methods
   */
  private validateSymbol(symbol: string): boolean {
    // Skip validation if symbol is empty string
    if (symbol === "") {
      return true;
    }
    if (!symbol || typeof symbol !== "string" || symbol.trim() === "") {
      return false;
    }
    // Basic validation: should contain "/" separator (e.g., "BTC/USDC")
    return symbol.includes("/");
  }

  private validateMarketId(marketId: string): boolean {
    if (marketId === null || marketId === undefined) {
      return false;
    }
    // Ensure it's a string
    const marketIdStr = String(marketId).trim();
    // Check it's not empty and not "0" (which might be invalid)
    // Note: "0" might be valid for some markets, but typically we want non-zero market IDs
    return marketIdStr !== "";
  }

  private validateKlineInterval(interval: string): boolean {
    const validIntervals = ["1m", "5m", "15m", "1h", "4h", "1d"];
    return validIntervals.includes(interval);
  }

  /**
   * Handle multi-feed messages and route them to appropriate callbacks
   * Supports both Router protocol (primary) and action-based protocol (backward compatibility)
   */
  private handleMultiFeedMessage(message: any): void {
    // Handle multi-feed messages: Router protocol { type: "data", channel: "multi_feed", sub_channel: "...", data: {...} } (primary)
    // or action-based { action: "data" | "update", channel: "multi_feed", sub_channel: "...", data: {...} } (backward compatibility)
    const isRouterProtocol = message.type === "data";
    const isActionBased =
      message.action === "data" || message.action === "update";

    if (
      (isActionBased || isRouterProtocol) &&
      message.channel === "multi_feed" &&
      message.sub_channel
    ) {
      // Type the message as MultiFeedRaweEnvelope
      const multiFeedMessage = message as MultiFeedRaweEnvelope;
      const subChannel = multiFeedMessage.sub_channel;

      // Get callbacks for this specific sub-channel
      const subChannelCallbacks =
        this.multiFeedSubscriptions.get(subChannel) || [];

      // Get all multi-feed callbacks (for general multi-feed subscription)
      const multiFeedCallbacks = this.multiFeedCallbacks;

      // Extract data from message - for multi-feed, data is already in message.data
      let data = multiFeedMessage.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("Failed to parse multi-feed data field:", e);
          return;
        }
      }

      if (data) {
        // Pass raw data without conversion - conversion is handled by consumer (e.g., useOrderbook hook)
        // Note: For orderbook sub-channel, price and size are satoshi strings that need conversion
        // but we let the consumer handle it for consistency
        let processedData = data;

        // Create typed message with envelope and processed data
        const typedMessage: MultiFeedMessage = {
          ...multiFeedMessage,
          data: processedData,
        };

        // Call sub-channel specific callbacks with just the processed data
        subChannelCallbacks.forEach((cb) => {
          try {
            cb(processedData);
          } catch (error) {
            console.error(
              `Error in multi-feed sub-channel callback for ${subChannel}:`,
              error,
            );
          }
        });

        // Call general multi-feed callbacks with typed message (includes envelope + data)
        multiFeedCallbacks.forEach((cb) => {
          try {
            cb(typedMessage);
          } catch (error) {
            console.error(`Error in multi-feed callback:`, error);
          }
        });
      }
    }
  }

  /**
   * Subscribe to multi-feed channel
   * Multi-feed provides a single subscription that streams updates from multiple sub-channels
   * @param marketId - Market ID to subscribe to (string). Use empty string "" for auth-only sub-channels like balances, orders, positions, buckets
   * @param callback - Callback function for multi-feed updates (receives { sub_channel, data })
   * @param params - Optional object with custom extra parameters (e.g., { symbol: "BTC/USDC", interval: "1m" } for kline)
   */
  subscribeMultiFeed(
    marketId: string,
    callback: SubscriptionCallback,
    params?: Record<string, any>,
  ): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    // Validate parameters - marketId can be empty string for auth-only sub-channels
    const hasMarketId = marketId !== "";
    if (hasMarketId && !this.validateMarketId(marketId)) {
      throw new Error(
        `Invalid market ID: ${marketId}. Market ID must be a non-empty string.`,
      );
    }
    // Validate symbol from params if provided
    if (params?.symbol && !this.validateSymbol(params.symbol)) {
      throw new Error(
        `Invalid symbol: ${params.symbol}. Symbol must be in format "BASE/QUOTE" (e.g., "BTC/USDC").`,
      );
    }
    // Validate interval from params if provided
    if (params?.interval && !this.validateKlineInterval(params.interval)) {
      throw new Error(
        `Invalid interval: ${params.interval}. Interval must be one of: "1m", "5m", "15m", "1h", "4h", "1d".`,
      );
    }

    const channel = "multi_feed";

    // Register multi-feed message handler if not already registered
    if (!this.multiFeedHandlerRegistered) {
      this.wsClient.on(channel, this.handleMultiFeedMessage.bind(this));
      this.multiFeedHandlerRegistered = true;
    }

    // Store callback - we'll route by sub_channel in the handler
    this.multiFeedCallbacks.push(callback);

    // Register pending subscription for acknowledgment tracking
    this.wsClient.registerPendingSubscription(channel);

    // Use special key for auth-only subscriptions (empty marketId)
    const subscriptionKey = hasMarketId ? marketId : "auth-only";

    // Send subscription message using Router protocol format
    // Only send if not already subscribed for this marketId (or auth-only)
    const isNewSubscription = !this.multiFeedSubscribedMarkets.has(subscriptionKey);
    if (isNewSubscription) {
      try {
        const subscribeMsg: Record<string, any> = {
          type: "subscribe",
          channel: "multi_feed",
          params: {},
        };
        // Only include market_id if provided
        if (hasMarketId) {
          subscribeMsg.params.market_id = marketId;
        }
        // Merge params into subscription message
        if (params) {
          Object.assign(subscribeMsg.params, params);
        }
        this.wsClient.send(subscribeMsg);
        this.multiFeedSubscribedMarkets.add(subscriptionKey);
        // Optionally wait for subscription acknowledgment (non-blocking)
        // Users can call waitForSubscription() if they need to wait
      } catch (error) {
        const index = this.multiFeedCallbacks.indexOf(callback);
        if (index > -1) {
          this.multiFeedCallbacks.splice(index, 1);
        }
        throw error;
      }
    }
  }

  /**
   * Subscribe to a specific sub-channel within multi-feed
   * This is a convenience method that filters multi-feed messages for a specific sub-channel
   * @param subChannel - Sub-channel name (e.g., "balances", "orders", "positions", "orderbook", etc.)
   * @param marketId - Market ID to subscribe to (string). Use empty string "" for auth-only sub-channels like "balances", "orders", "positions", "buckets" - these only require authentication
   * @param callback - Callback function for sub-channel updates
   * @param params - Optional object with custom extra parameters for different sub channels (e.g., { interval: "1m" } for kline, { symbol: "BTC/USDC" } if needed)
   */
  subscribeMultiFeedSubChannel(
    subChannel: string,
    marketId: string,
    callback: SubscriptionCallback,
    params?: Record<string, any>,
  ): void {
    // Register callback for this sub-channel
    if (!this.multiFeedSubscriptions.has(subChannel)) {
      this.multiFeedSubscriptions.set(subChannel, []);
    }
    this.multiFeedSubscriptions.get(subChannel)!.push(callback);

    // Determine subscription key: use marketId if provided (non-empty), otherwise use "auth-only" for auth-only sub-channels
    const hasMarketId = marketId !== "";
    const subscriptionKey = hasMarketId ? marketId : "auth-only";

    // Subscribe to multi-feed if not already subscribed for this marketId (or auth-only)
    // Use a no-op callback that we can track - the actual routing is handled by the multi-feed handler
    if (!this.multiFeedSubscribedMarkets.has(subscriptionKey)) {
      const noOpCallback: SubscriptionCallback = () => {
        // No-op: sub-channel callbacks are handled by the multi-feed handler
        // This callback is only used to track the subscription state
      };
      this.subscribeMultiFeed(marketId, noOpCallback, params);
    }
    // If already subscribed, the multi-feed handler will route messages to sub-channel callbacks
  }

  /**
   * Unsubscribe from multi-feed channel
   */
  unsubscribeMultiFeed(
    _marketId: string,
    _symbol: string,
    callback?: SubscriptionCallback,
  ): void {
    if (callback) {
      const index = this.multiFeedCallbacks.indexOf(callback);
      if (index > -1) {
        this.multiFeedCallbacks.splice(index, 1);
      }
    } else {
      this.multiFeedCallbacks = [];
    }

    // If no more multi-feed subscriptions, clear sub-channel subscriptions
    if (this.multiFeedCallbacks.length === 0) {
      // Note: We can't easily remove the handler without tracking it, so we'll leave it
      // The handler will just have no callbacks to call
      this.multiFeedSubscriptions.clear();
      // Clear subscribed markets tracking when no more subscriptions
      this.multiFeedSubscribedMarkets.clear();
    }

    // Send unsubscribe message if no more callbacks and connected
    if (this.multiFeedCallbacks.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "multi_feed",
        });
        // Clear subscribed markets when unsubscribing
        this.multiFeedSubscribedMarkets.clear();
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Unsubscribe from a specific multi-feed sub-channel
   */
  unsubscribeMultiFeedSubChannel(
    subChannel: string,
    callback?: SubscriptionCallback,
  ): void {
    const callbacks = this.multiFeedSubscriptions.get(subChannel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        if (callbacks.length === 0) {
          this.multiFeedSubscriptions.delete(subChannel);
        } else {
          this.multiFeedSubscriptions.set(subChannel, callbacks);
        }
      }
    } else {
      this.multiFeedSubscriptions.delete(subChannel);
    }
  }

  /**
   * Clean up all subscriptions (called when disconnecting)
   */
  disconnect(): void {
    this.multiFeedSubscriptions.clear();
    this.multiFeedCallbacks = [];
    this.multiFeedHandlerRegistered = false;
    this.multiFeedSubscribedMarkets.clear();
  }
}
